// What was handled, and what was ignored — by whom, and when.
//
// Owner, 2026-09-17: « Record who clicked and when. A place where I can see
// what was handled and what was ignored — neither should be a black hole. »
// Both are reversible from here: re-open a handled row, stop ignoring a target.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pilotageApi, ordersApi } from '@/lib/api';
import { useT } from '@/lib/i18n';

const when = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

/**
 * `drawer` (owner, 2026-10-07): « Handled and ignored » is a button beside the
 * Pipeline's filters that opens a side drawer, instead of a block under the
 * lanes. `inline` keeps the old folding block.
 */
export function PilotageHistory({ variant = 'inline' }: { variant?: 'inline' | 'drawer' } = {}) {
  const { t } = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const handled = useQuery({ queryKey: ['pilotage-history', 'handled'], queryFn: () => pilotageApi.handled(), enabled: open });
  const ignored = useQuery({ queryKey: ['pilotage-history', 'ignored'], queryFn: () => ordersApi.ignored(), enabled: open });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['pilotage-history'] }); qc.invalidateQueries({ queryKey: ['pilotage'] }); };
  const reopen = useMutation({ mutationFn: pilotageApi.reopenHandled, onSuccess: refresh });
  const unignore = useMutation({ mutationFn: pilotageApi.unignore, onSuccess: refresh });

  const h = handled.data?.items ?? [];
  const g = ignored.data?.items ?? [];

  const lists = (
        <div className={variant === 'drawer' ? 'px-5 py-4 grid gap-5' : 'px-4 pb-4 grid gap-4 md:grid-cols-2 storiz-fade-in'}>
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-wider text-green-700 mb-1.5">✓ {t('Traités')} · {h.length}</p>
            {handled.isLoading && <p className="text-[12px] text-gray-400">{t('Chargement…')}</p>}
            {!handled.isLoading && h.length === 0 && <p className="text-[12px] text-gray-400">{t('Rien pour l’instant.')}</p>}
            <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
              {h.map((i) => (
                <li key={i.id} className="py-2 flex items-start gap-2">
                  <span className="min-w-0 flex-1">
                    <span className="block text-[12px] font-semibold text-gray-800 truncate">{i.name ?? i.ref}{i.orderName ? ` · ${i.orderName}` : ''}</span>
                    <span className="block text-[11px] text-gray-500">{i.laneLabel} · {i.handled_by ?? '—'} · {when(i.handled_at)}</span>
                  </span>
                  <button
                    type="button" disabled={reopen.isPending}
                    onClick={() => reopen.mutate({ kind: i.kind, ref: i.ref, lane: i.lane })}
                    className="text-[11px] font-semibold px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors duration-150 shrink-0"
                  >{t('Rouvrir')}</button>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-wider text-red-700 mb-1.5">✗ {t('Ignorés')} · {g.length}</p>
            {ignored.isLoading && <p className="text-[12px] text-gray-400">{t('Chargement…')}</p>}
            {!ignored.isLoading && g.length === 0 && <p className="text-[12px] text-gray-400">{t('Rien pour l’instant.')}</p>}
            <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
              {g.map((i) => (
                <li key={i.id} className="py-2 flex items-start gap-2">
                  <span className="min-w-0 flex-1">
                    <span className="block text-[12px] font-semibold text-gray-800 truncate">
                      {i.order?.customer_name ?? i.ref.split('@')[0]}{i.order ? ` · ${i.order.shopify_order_name}` : ''}
                    </span>
                    <span className="block text-[11px] text-gray-500 truncate">{i.ignored_by ?? '—'} · {when(i.ignored_at)}{i.reason ? ` · ${i.reason}` : ''}</span>
                  </span>
                  <button
                    type="button" disabled={unignore.isPending}
                    onClick={() => unignore.mutate({ kind: i.kind, ref: i.ref })}
                    className="text-[11px] font-semibold px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors duration-150 shrink-0"
                  >{t('Ne plus ignorer')}</button>
                </li>
              ))}
            </ul>
          </div>
        </div>
  );

  if (variant === 'drawer') {
    return (<>
      <button type="button" onClick={() => setOpen(true)} aria-haspopup="dialog"
        className="inline-flex items-center gap-1.5 min-h-[44px] md:min-h-0 md:h-10 px-3 rounded-lg border border-border-default bg-bg-card text-[12.5px] text-text-secondary hover:bg-bg-elevated cursor-pointer">
        {t('Traités et ignorés')}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-text-primary/30 flex justify-end" onClick={() => setOpen(false)}>
          <aside role="dialog" aria-modal="true" aria-label={t('Traités et ignorés')}
            className="w-full max-w-md h-full bg-bg-card border-l border-border-subtle overflow-y-auto storiz-fade-in" onClick={(e) => e.stopPropagation()}>
            <header className="sticky top-0 z-10 bg-bg-card px-5 pt-5 pb-3 border-b border-border-subtle flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading text-[20px] leading-tight text-text-primary">{t('Traités et ignorés')}</h3>
                <p className="text-[11.5px] text-text-tertiary mt-0.5">{t('Qui a cliqué, et quand — tout est réversible')}</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-[12.5px] text-text-secondary cursor-pointer shrink-0">{t('Fermer')}</button>
            </header>
            {lists}
          </aside>
        </div>
      )}
    </>);
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <button
        type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left cursor-pointer hover:bg-gray-50/60 rounded-xl transition-colors duration-150"
      >
        <span>
          <span className="block text-[13px] font-semibold text-gray-900">{t('Traités et ignorés')}</span>
          <span className="block text-[11px] text-gray-500">{t('Qui a cliqué, et quand — tout est réversible')}</span>
        </span>
        <span className={`text-gray-400 text-[12px] transition-transform duration-150 ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>

      {open && lists}
    </div>
  );
}
